"use client";

import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { Button, Dialog, DialogContent, DialogTitle } from "@ubud-ui/react-ui-kit";
import { useBoolean } from "@shared/hooks";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

export const PopUp = () => {
  const { value: isOpen, on: onOpen, off: onClose } = useBoolean(false);

  const config = useClientConfig();
  const popUpData = config?.layoutSetting?.features?.popUp;

  const [countdown, setCountdown] = useState<number | null>(popUpData?.secondsToHide || null);

  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleClose = () => {
    onClose();
    setCountdown(null);
  };

  useEffect(() => {
    if (!popUpData) return;

    if (window.__POPUP_HAS_SHOWN__) {
      return;
    }

    showTimeoutRef.current = setTimeout(
      () => {
        onOpen();

        window.__POPUP_HAS_SHOWN__ = true;

        if (popUpData.secondsToHide) {
          setCountdown(popUpData.secondsToHide);
        }
      },
      (popUpData.secondsToShow || 0) * 1000,
    );

    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
    };
  }, [popUpData]);

  useEffect(() => {
    if (!isOpen || popUpData?.secondsToHide == null) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) {
          setTimeout(handleClose, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  if (!popUpData) {
    return null;
  }

  const { image, link } = popUpData;

  const imageEl = (
    <Image
      priority
      src={image?.url ?? ""}
      alt={image?.alternativeText || "Popup Image"}
      width={600}
      height={400}
      className="w-full h-auto object-contain object-top rounded-2xl"
      style={{
        display: "block",
        objectFit: "cover",
      }}
    />
  );

  return (
    <Dialog open={isOpen} defaultOpen={isOpen} onOpenChange={onClose} modal={true}>
      <DialogContent hideCloseButton={true} className="max-h-[100vh] overflow-y-auto sm:max-w-full !bg-transparent p-0 m-0">
        <Button
          variant="outline"
          className="bg-white text-gray-900 size-10 sm:size-12 fixed top-6 right-4 md:right-6 lg:top-10 lg:right-10"
          onClick={() => onClose()}
        >
          <XMarkIcon className="size-6" />
        </Button>
        <DialogTitle className="hidden"></DialogTitle>

        <div className="mx-auto w-full md:w-[480px] py-6 px-4 sm:px-6 mt-8 h-[calc(100vh_-_32px)] flex flex-col items-center justify-center">
          {countdown && popUpData.secondsToHide && (
            <div className="text-white text-left w-full mb-4 sm:mb-6">
              <div className="text-base font-normal">Menutup dalam {countdown} detik</div>
            </div>
          )}
          <div className="relative overflow-hidden rounded-2xl">
            {image && link ? (
              <Link href={link.href} target={link.target}>
                {imageEl}
              </Link>
            ) : image ? (
              imageEl
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
